import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const ScheduledMaintenance = ({ user }) => {
  const [scheduled, setScheduled] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    equipmentId: '',
    scheduledDate: new Date().toISOString().split('T')[0],
    frequency: 'monthly',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scheduledRes, equipmentRes] = await Promise.all([
        axiosInstance.get('/scheduled'),
        axiosInstance.get('/equipment')
      ]);
      setScheduled(scheduledRes.data);
      setEquipment(equipmentRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/scheduled', formData);
      toast.success('Manutenzione pianificata!');
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const resetForm = () => {
    setFormData({
      equipmentId: '',
      scheduledDate: new Date().toISOString().split('T')[0],
      frequency: 'monthly',
      description: ''
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const frequencyColors = {
    daily: 'badge-red',
    weekly: 'badge-yellow',
    monthly: 'badge-blue',
    yearly: 'badge-green'
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="scheduled-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Pianificazione</h1>
          <p className="text-gray-600">Manutenzioni programmate</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                data-testid="add-scheduled-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Pianificazione
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="scheduled-dialog">
              <DialogHeader>
                <DialogTitle>Nuova Pianificazione</DialogTitle>
                <DialogDescription>
                  Programma una manutenzione ricorrente
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="equipmentId">Impianto</Label>
                  <select
                    id="equipmentId"
                    value={formData.equipmentId}
                    onChange={(e) => setFormData({ ...formData, equipmentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="scheduled-equipment-select"
                  >
                    <option value="">Seleziona impianto</option>
                    {equipment.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="scheduledDate">Data Inizio</Label>
                  <Input
                    id="scheduledDate"
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                    required
                    data-testid="scheduled-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="frequency">Frequenza</Label>
                  <select
                    id="frequency"
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="scheduled-frequency-select"
                  >
                    <option value="daily">Giornaliera</option>
                    <option value="weekly">Settimanale</option>
                    <option value="monthly">Mensile</option>
                    <option value="yearly">Annuale</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                    required
                    data-testid="scheduled-description-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="scheduled-submit-button"
                >
                  Pianifica
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {scheduled.map((item) => (
          <Card key={item.id} className="card-hover shadow-lg border-0" data-testid={`scheduled-card-${item.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 mr-3">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{item.equipmentName}</CardTitle>
                    <span className={`badge ${frequencyColors[item.frequency]} mt-1`}>
                      {item.frequency}
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700 mb-3">{item.description}</p>
              <div className="space-y-1 text-sm">
                <p className="text-gray-600">
                  <span className="font-medium">Prossima scadenza:</span>{' '}
                  {new Date(item.nextDue).toLocaleDateString('it-IT')}
                </p>
                {item.lastCompleted && (
                  <p className="text-gray-600">
                    <span className="font-medium">Ultima esecuzione:</span>{' '}
                    {new Date(item.lastCompleted).toLocaleDateString('it-IT')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ScheduledMaintenance;